// Shared types for the forge shell + its verb tabs.
//
// Every verb follows the same shape: given an AudioContext + storage,
// produce an AudioBuffer + SampleOrigin and hand it to the shell via
// onBuffer. The shell takes it from there (trim/commit). New verbs drop
// in by implementing VerbComponent.

import type { SampleOrigin } from '../types';
import type { SampleStorage } from '../storage/idb';

export type VerbId =
  | 'import'
  | 'record'
  | 'bounce'
  | 'synth'
  | 'chop'
  | 'mangle'
  | 'speech'
  | 'draw'
  | 'convolve'
  | 'sidechain'
  | 'stretch'
  | 'freeze'
  | 'pitch';

export type VerbMeta = {
  /** Derived sample name, pre-fills the commit-bar name field. */
  suggestedName?: string;
  /** Provenance record persisted with the Sample. */
  origin: SampleOrigin;
  /** Defaulted from the buffer; overrides set pitchedRootMidi on commit. */
  pitchedRootMidi?: number;
  /** IDs of any IDB-resident samples consumed to produce this buffer.
   *  Used by lineage views — see Sample.sourceSampleIds. */
  sourceSampleIds?: string[];
};

export type VerbProps = {
  audioContext: AudioContext | null;
  storage: SampleStorage | null;
  /** Hand off the produced buffer + meta. The shell renders it in
   *  WaveformPad and surfaces the commit bar. */
  onBuffer: (buffer: AudioBuffer, meta: VerbMeta) => void;
  /** Status reporter so long-running verbs (record, bounce) can log
   *  progress into the shell's footer. */
  onStatus: (message: string, level?: 'ok' | 'warn' | 'err' | '') => void;
};

export type VerbDefinition = {
  id: VerbId;
  label: string;
  description: string;
  hotkey: string;
};

export const VERB_DEFINITIONS: VerbDefinition[] = [
  { id: 'import', label: 'Import', description: 'Drop or pick an audio file', hotkey: '1' },
  { id: 'record', label: 'Record', description: 'Capture the mic', hotkey: '2' },
  { id: 'bounce', label: 'Bounce', description: 'Capture cycles of the running pattern', hotkey: '3' },
  { id: 'synth', label: 'Synth', description: 'Bake a synth hit', hotkey: '4' },
  { id: 'chop', label: 'Chop', description: 'Slice a longer source', hotkey: '5' },
  { id: 'mangle', label: 'Mangle', description: 'Transform an existing sample', hotkey: '6' },
  { id: 'speech', label: 'Speech', description: 'Text-to-speech sample', hotkey: '7' },
  { id: 'draw', label: 'Draw', description: 'Scribble a wavetable', hotkey: '8' },
  { id: 'convolve', label: 'Convolve', description: 'Source × IR — apply one sample as the impulse response of another', hotkey: '9' },
  { id: 'sidechain', label: 'Sidechain', description: 'Duck a source by the envelope of a key signal', hotkey: '0' },
  { id: 'stretch', label: 'Stretch', description: 'Auto-detect BPM and stretch to a target tempo', hotkey: '-' },
  { id: 'freeze', label: 'Freeze', description: 'Hold a moment in a sample and stretch it via granular scatter', hotkey: '=' },
  { id: 'pitch', label: 'Pitch', description: 'Auto-detect pitch and resample to a target note', hotkey: '[' },
];
